import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ErrorAlert from '../../components/common/ErrorAlert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from './AuthContext';
import axios from '../../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { ApiResult, LoginResultDto, UserDto, LoginRequest, RegisterRequest } from '../../types/auth';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Registration state
  const [registerOpen, setRegisterOpen] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Use environment variable for backend API URL
      const response = await axios.post<ApiResult<LoginResultDto>>('/api/auth/login', { 
        email: email, 
        password: password 
      } as LoginRequest);
      
      console.log('Login response:', response.data);
      
      if (response.data.success && response.data.data?.token) {
        const token = response.data.data.token;
        login(token);
        setTimeout(() => navigate('/inicio'), 0);
      } else {
        setError(response.data.message || 'Não foi possível entrar');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Não foi possível entrar');
    } finally {
      setLoading(false);
    }
  };

  // Registration submit handler
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegLoading(true);
    setRegError('');
    try {
      const response = await axios.post<ApiResult<UserDto>>('/api/auth/register', { 
        email: regEmail, 
        password: regPassword 
      } as RegisterRequest);
      
      if (response.data.success) {
        // Registration successful - now login
        const loginResponse = await axios.post<ApiResult<LoginResultDto>>('/api/auth/login', { 
          email: regEmail, 
          password: regPassword 
        } as LoginRequest);
        
        if (loginResponse.data.success && loginResponse.data.data?.token) {
          const token = loginResponse.data.data.token;
          login(token);
          setRegisterOpen(false);
          setTimeout(() => navigate('/inicio'), 0);
        } else {
          setRegError('A conta foi criada, mas não foi possível entrar');
        }
      } else {
        setRegError(response.data.message || 'Não foi possível criar a conta');
      }
    } catch (err: any) {
      setRegError(err.response?.data?.message || 'Não foi possível criar a conta');
    } finally {
      setRegLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" px={2}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 400 }}>
        <Typography variant="h5" mb={2}>Entrar</Typography>
        {error && <ErrorAlert message={error} />}
        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="email"
          />
          <TextField
            label="Palavra-passe"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="current-password"
          />
          <Box mt={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
              {loading ? <LoadingSpinner /> : 'Entrar'}
            </Button>
            <Button variant="text" color="secondary" fullWidth sx={{ mt: 1 }} onClick={() => setRegisterOpen(true)}>
              Criar conta
            </Button>
            <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={() => navigate('/mobile-app')}>
              Obter a app Android
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Registration Dialog */}
      <Dialog open={registerOpen} onClose={() => setRegisterOpen(false)}>
        <DialogTitle>Criar conta</DialogTitle>
        <DialogContent>
          {regError && <ErrorAlert message={regError} />}
          <form id="register-form" onSubmit={handleRegister}>
            <TextField
              label="Email"
              value={regEmail}
              onChange={e => setRegEmail(e.target.value)}
              fullWidth
              margin="normal"
              required
              autoComplete="email"
            />
            <TextField
              label="Palavra-passe"
              type="password"
              value={regPassword}
              onChange={e => setRegPassword(e.target.value)}
              fullWidth
              margin="normal"
              required
              autoComplete="new-password"
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterOpen(false)} disabled={regLoading}>Cancelar</Button>
          <Button type="submit" form="register-form" variant="contained" color="primary" disabled={regLoading}>
            {regLoading ? <LoadingSpinner /> : 'Criar conta'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
